import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ACTIONS from "../../../redux/action";
import axios from "../../../shared/axios/axiosProxy";
import i18n from "../../../utils/i18n";
import { isEntrepreneurTaxCompanyType } from "../../searchFlowVersionB/steps/ev/EvTaxForm.jsx";
import classes from "./regTaxForm.module.scss";

const ATALANY_SUB_OPTIONS = [
  {
    value: "singlesecondary",
    title: i18n.t("searchflow.secondaryemployment"),
    subtitle: i18n.t("searchflow.regsecondaryemploymentsubtitle"),
  },
  {
    value: "kiva",
    title: i18n.t("searchflow.primaryemployment"),
    subtitle: i18n.t("searchflow.regprimaryemploymentsubtitle"),
  },
];

export const REG_TAX_FORM_UNSURE_VALUE = "idk";

const STANDALONE_OPTIONS = [
  { value: "singleprimary", title: i18n.t("providervafform.singleprimary") },
  { value: "vszja", title: i18n.t("searchflow.vszja") },
  {
    value: REG_TAX_FORM_UNSURE_VALUE,
    title: i18n.t("searchflow.regtaxformunsuretitle"),
    subtitle: i18n.t("searchflow.regtaxformunsuresubtitle"),
  },
];

const STANDALONE_OPTION_VALUES = STANDALONE_OPTIONS.map((option) => option.value);

export const isRegTaxFormSelectionValid = (companytype) =>
  isEntrepreneurTaxCompanyType(companytype) || companytype === REG_TAX_FORM_UNSURE_VALUE;

const Radio = ({ checked }) => (
  <span
    className={`${classes.radio} ${checked ? classes.radioChecked : ""}`}
    aria-hidden="true"
  />
);

const RegTaxForm = ({ onInteractedChange, initialInteracted = false } = {}) => {
  const companyStore = useSelector((state) => state.company_store);
  const dispatch = useDispatch();
  const companytype = companyStore.companytype;
  const isAtalanySubValue =
    companytype === "kiva" || companytype === "singlesecondary";

  // Same "don't trust a stale default as a real pick" rule as EvTaxForm — the group
  // starts collapsed/unselected until the user actually clicks something here.
  const [atalanyExpanded, setAtalanyExpanded] = useState(
    initialInteracted && isAtalanySubValue,
  );
  const [hasInteracted, setHasInteracted] = useState(initialInteracted);

  useEffect(() => {
    onInteractedChange?.(hasInteracted);
  }, [hasInteracted, onInteractedChange]);

  const isAtalanyGroupSelected = atalanyExpanded;

  const syncSearchTaxFields = useCallback((searchID, nextCompanytype, taxform) => {
    if (!searchID) {
      return;
    }
    axios
      .patch(
        `/api/search/${searchID}`,
        { companytype: nextCompanytype, taxform },
        { headers: { "Content-Type": "application/json" } },
      )
      .catch((error) => {
        console.error(
          "Error saving taxform to searchSchema:",
          error.response?.data || error.message,
        );
      });
  }, []);

  const updateCompanyType = useCallback(
    (nextCompanytype) => {
      const taxform = "";

      setHasInteracted(true);
      dispatch(
        ACTIONS.addCompanyInfo({
          id: "companytype",
          data: nextCompanytype,
          stateObject: "company_store",
        }),
      );
      dispatch(
        ACTIONS.addCompanyInfo({
          id: "taxform",
          data: taxform,
          stateObject: "company_store",
        }),
      );
      syncSearchTaxFields(companyStore.searchID, nextCompanytype, taxform);
    },
    [companyStore.searchID, dispatch, syncSearchTaxFields],
  );

  const clearCompanyTypeSelection = useCallback(() => {
    setHasInteracted(true);
    dispatch(
      ACTIONS.addCompanyInfo({
        id: "companytype",
        data: "",
        stateObject: "company_store",
      }),
    );
    dispatch(
      ACTIONS.addCompanyInfo({
        id: "taxform",
        data: "",
        stateObject: "company_store",
      }),
    );
    syncSearchTaxFields(companyStore.searchID, "", "");
  }, [companyStore.searchID, dispatch, syncSearchTaxFields]);

  const handleAtalanyHeaderClick = () => {
    setAtalanyExpanded(true);
    if (STANDALONE_OPTION_VALUES.includes(companytype)) {
      clearCompanyTypeSelection();
    }
  };

  const handleAtalanySubSelect = (value) => {
    setAtalanyExpanded(true);
    updateCompanyType(value);
  };

  const handleStandaloneSelect = (value) => {
    setAtalanyExpanded(false);
    updateCompanyType(value);
  };

  return (
    <div className={classes.root}>
      <div className={classes.scrollArea}>
        <div className={classes.content}>
          <div className={classes.mainColumn}>
            <header className={classes.header}>
              <h1 className={classes.title}>{i18n.t("searchflow.regtaxformtitle")}</h1>
              <p className={classes.subtitle}>
                {i18n.t("searchflow.regtaxformsubtitle")}
              </p>
            </header>

            <div className={classes.rightColumn}>
              <div className={classes.cardsContainer}>
                <div
                  className={`${classes.card} ${classes.cardExpandable} ${atalanyExpanded ? classes.cardExpandableExpanded : ""} ${isAtalanyGroupSelected ? classes.selectedRow : ""}`}
                >
                  <button
                    type="button"
                    className={classes.cardHeader}
                    onClick={handleAtalanyHeaderClick}
                    aria-expanded={atalanyExpanded}
                    aria-pressed={isAtalanyGroupSelected}
                  >
                    <div className={classes.cardContent}>
                      <div className={classes.cardTitle}>{i18n.t("companylabels.evatalany")}</div>
                    </div>
                    <Radio checked={isAtalanyGroupSelected} />
                  </button>

                  <div className={classes.subOptions} aria-hidden={!atalanyExpanded}>
                    {ATALANY_SUB_OPTIONS.map((option) => {
                      const isSelected = hasInteracted && companytype === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          className={`${classes.subOption} ${isSelected ? classes.subOptionSelected : ""}`}
                          onClick={() => handleAtalanySubSelect(option.value)}
                          aria-pressed={isSelected}
                          tabIndex={atalanyExpanded ? 0 : -1}
                        >
                          <div className={classes.subOptionContent}>
                            <div className={classes.subOptionTitle}>{option.title}</div>
                            <div className={classes.subOptionSubtitle}>{option.subtitle}</div>
                          </div>
                          <Radio checked={isSelected} />
                        </button>
                      );
                    })}
                  </div>
                </div>

                {STANDALONE_OPTIONS.map((option) => {
                  const isSelected = hasInteracted && companytype === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={`${classes.card} ${isSelected ? classes.selectedRow : ""}`}
                      onClick={() => handleStandaloneSelect(option.value)}
                      aria-pressed={isSelected}
                    >
                      <div className={classes.cardContent}>
                        <div className={classes.cardTitle}>{option.title}</div>
                        {option.subtitle && (
                          <div className={classes.cardSubtitle}>{option.subtitle}</div>
                        )}
                      </div>
                      <Radio checked={isSelected} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegTaxForm;
